import { GetWSMgr } from "@/Utils/StateLib";
import { enumOverrideType } from "@coreapp/constants";
import { Globals } from "@coreapp/CoreApp/Globals";
import { QChange } from "@coreapp/CoreApp/QChange";
import { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import { QTalentSearchRFC } from "@coreapp/CoreApp/QTalentSearchRFC";
import { QUpdateTalentRFC } from "@coreapp/CoreApp/QUpdateTalentRFC";

export const addTalentToPlan = (planMgr, talentId, mode, branchAdd = false) => {
  if (planMgr.GetPlanStatus() !== "Active") {
    return;
  }

  const wsMgr = GetWSMgr();
  const doc = planMgr.GetDoc();
  const objDB = doc.GetObjDB();
  const boxClass = objDB.GetClassByName("Box");
  const IDFieldDef = boxClass.GetRecordIDFieldDef(false);
  const fieldName = IDFieldDef.GetFieldName();

  const talentPool = planMgr.GetTalentPool();
  const theTalentPoolObj = talentPool.find(x => x[fieldName] === talentId);

  if (!theTalentPoolObj) {
    return;
  }

  const changeType = theTalentPoolObj["__inplan__"] ? enumOverrideType.move : enumOverrideType.add;
  const ID = theTalentPoolObj[fieldName];
  let overrideDBObj = planMgr.ObjectIDTrackedByType(ID, changeType);

  const dbObj = new QObjDBObject();
  dbObj.InitObject(boxClass);
  boxClass.ProcessObject(dbObj, theTalentPoolObj);

  if (changeType === enumOverrideType.move) {
    dbObj.fCloneBase = dbObj.Clone(true);
  }

  const scm = doc.GetSubChartMgr();
  const curSubChart = scm.GetCurrentSubChart();
  const topBox = curSubChart.GetTopDBObj();

  dbObj.fIsInPlan = theTalentPoolObj["__inplan__"];

  if (overrideDBObj == null) {
    const objType = dbObj.InferObjectType();
    overrideDBObj = planMgr.CreateOverride(
      changeType,
      objType,
      ID,
      "",
      [],
      undefined,
      undefined,
      !dbObj.fIsInPlan
    );
  }

  // Initialize the override with a change to the current top box
  if (
    topBox.GetObjectRecordID() !== dbObj.GetObjectRecordID() &&
    changeType !== enumOverrideType.move
  ) {
    const overrideClass = planMgr?.fOverrideClass;

    const parentIdFieldDef = boxClass.GetParentIDFieldDef();
    const parentIdFieldName = parentIdFieldDef?.GetFieldName() ?? "";
    const changeDict = {
      data: {
        [parentIdFieldName]: topBox.GetObjectRecordID()
      }
    };
    const moveChange = new QChange();
    moveChange.InitChange(wsMgr, changeDict, overrideDBObj, dbObj, overrideClass);
    moveChange.UpdateChange(changeDict, enumOverrideType.change);
    moveChange.fPlanMgr = planMgr;
    overrideDBObj.fChanges.push(moveChange);
  }

  planMgr.handleRecord(overrideDBObj, dbObj, mode, branchAdd);
};

export const addResultToTalentPool = (planMgr, result) => {
  return new Promise((resolve, reject) => {
    const wsMgr = GetWSMgr();
    if (!wsMgr || !planMgr) {
      reject();
      return;
    }

    const objDB = wsMgr.GetDoc().GetObjDB();
    const boxClass = objDB.GetClassByName("Box");
    const personIDDef = boxClass.GetFieldDefByID("PersonID");
    const personIDDefName = personIDDef.GetFieldName();

    const data = {
      planID: planMgr.GetPlanID(),
      action: "savetalent",
      employee: {
        ID: result[personIDDefName] ?? result["PersonID"],
        inPlan: result["isPlan"] === true
      }
    };
    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();

    const callbackObj = {
      fPlanMgr: planMgr,
      resolve,
      reject
    };

    // send with indexPos updated, create if none exists grab talent list and update indexes?
    const setTalentRFC = new QUpdateTalentRFC();
    setTalentRFC.InitSetPool(callbackObj);
    setTalentRFC.SetParams(accountName, user, JSON.stringify(data), "talentpool");
    Globals._rfcMgr.QueueRFC(setTalentRFC);
  });
};

export const removeTalentFromPlan = (planMgr, talentId) => {
  return new Promise((resolve, reject) => {
    const wsMgr = GetWSMgr();
    if (!wsMgr || !planMgr) {
      reject();
      return;
    }

    const data = { planID: planMgr.GetPlanID(), employeeID: talentId, action: "removetalent" };
    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();

    const callbackObj = {
      fPlanMgr: planMgr,
      resolve,
      reject
    };

    const removeTalentRFC = new QUpdateTalentRFC();
    removeTalentRFC.InitSetPool(callbackObj);
    removeTalentRFC.SetParams(accountName, user, JSON.stringify(data), "talentpool");
    Globals._rfcMgr.QueueRFC(removeTalentRFC);
  });
};

export const drainTalentPool = planMgr => {
  return new Promise((resolve, reject) => {
    const wsMgr = GetWSMgr();
    if (!wsMgr || !planMgr) {
      reject();
      return;
    }

    const data = { planID: planMgr.GetPlanID() };
    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();

    const callbackObj = {
      fPlanMgr: planMgr,
      resolve,
      reject
    };

    const removeTalentRFC = new QUpdateTalentRFC();
    removeTalentRFC.InitSetPool(callbackObj);
    removeTalentRFC.SetParams(accountName, user, JSON.stringify(data), "draintalentpool");
    Globals._rfcMgr.QueueRFC(removeTalentRFC);
  });
};

export const searchTalent = (planMgr, queryString, options) => {
  return new Promise((resolve, reject) => {
    const wsMgr = GetWSMgr();
    if (!wsMgr || !planMgr) {
      return reject();
    }

    const data = { query: queryString, planID: planMgr.GetPlanID(), options };
    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();

    const callBackObj = {
      resolve,
      reject
    };

    const setTalentRFC = new QTalentSearchRFC();
    setTalentRFC.InitTalentSearch(callBackObj);
    setTalentRFC.SetParams(accountName, user, JSON.stringify(data));
    Globals._rfcMgr.QueueRFC(setTalentRFC);
  });
};
